/**
 * Migration utility to move from scattered localStorage keys to Pinia stores
 * This runs once on app initialization to migrate legacy data
 */
import { useAuthStore } from '../stores/auth'
import { useCustomerStore } from '../stores/customer'
import { useAccountOpeningStore } from '../stores/accountOpening'

// Map of legacy localStorage keys to Pinia store actions
const LEGACY_KEY_MAP = {
  // Auth store
  'access_token': { store: 'auth', action: 'setAccessToken' },
  'access-token': { store: 'auth', action: 'setAccessToken' },
  'userId': { store: 'auth', field: 'user', transform: (v, state) => ({ ...state.user, id: v }) },
  'user_id': { store: 'auth', field: 'user', transform: (v, state) => ({ ...state.user, id: v }) },
  'phone': { store: 'auth', action: 'setPhone' },
  'phone-number': { store: 'auth', action: 'setPhone' },
  'customer-status': { store: 'auth', action: 'setCustomerStatus' },
  'isPhoneParamProvided': { store: 'auth', action: 'setIsPhoneParamProvided', transform: (v) => v === 'true' },

  // Customer store
  'first-name': { store: 'customer', field: 'firstName' },
  'last-name': { store: 'customer', field: 'lastName' },
  'identity-doc-no': { store: 'customer', field: 'identityDocNo' },
  'email': { store: 'customer', field: 'email' },
  'title': { store: 'customer', field: 'title' },
  'gender': { store: 'customer', field: 'gender' },
  'maritalStatus': { store: 'customer', field: 'maritalStatus' },
  'dateOfBirth': { store: 'customer', field: 'dateOfBirth' },
  'street': { store: 'customer', field: 'street' },
  'town': { store: 'customer', field: 'town' },
  'branch': { store: 'customer', field: 'branch' },
  'currency': { store: 'customer', field: 'currency' },
  'monthlyIncomeAmount': { store: 'customer', field: 'monthlyIncomeAmount' },
  'sourceOfIncome': { store: 'customer', field: 'sourceOfIncome' },
  'lineOfBusiness': { store: 'customer', field: 'lineOfBusiness' },
  'activityExpectedOnAccount': { store: 'customer', field: 'activityExpectedOnAccount' },

  // Account Opening store
  'accountType': { store: 'accountOpening', action: 'setAccountType' },
  'accounts-type': { store: 'accountOpening', action: 'setAccountType' },
  'preAccountType': { store: 'accountOpening', action: 'setPreAccountType' },
  'currencyType': { store: 'accountOpening', action: 'setCurrencyType' },
  'currency-type': { store: 'accountOpening', action: 'setCurrencyType' },
  'active_account_types': {
    store: 'accountOpening',
    action: 'setActiveAccountTypes',
    transform: (v) => {
      try {
        return JSON.parse(v);
      } catch {
        return [];
      }
    }
  },
  'signature': { store: 'accountOpening', action: 'setSignature' },
};

// Keys that should be removed for security reasons (never migrate these)
const SENSITIVE_KEYS = ['password', 'confirmPassword', 'pin'];

// All legacy keys that should be removed after migration
const ALL_LEGACY_KEYS = [
  ...Object.keys(LEGACY_KEY_MAP),
  ...SENSITIVE_KEYS,
  'uid', // duplicate of uuid
  'uuid',
];

/**
 * Check if migration has already been completed
 */
function isMigrationComplete() {
  return localStorage.getItem('sb_sso_migrated_pinia') === 'true';
}

/**
 * Mark migration as complete
 */
function markMigrationComplete() {
  localStorage.setItem('sb_sso_migrated_pinia', 'true');
}

/**
 * Get Pinia store instance by name
 */
function getStore(storeName) {
  switch (storeName) {
    case 'auth':
      return useAuthStore();
    case 'customer':
      return useCustomerStore();
    case 'accountOpening':
      return useAccountOpeningStore();
    default:
      return null;
  }
}

/**
 * Migrate legacy localStorage data to Pinia stores
 */
export function migrateLocalStorage() {
  // Skip if already migrated
  if (isMigrationComplete()) {
    console.log('[Migration] Already completed, skipping...');
    return;
  }

  console.log('[Migration] Starting localStorage migration to Pinia...');

  // Collect customer details for batch update
  const customerDetails = {};
  let hasMigrated = false;

  // Process each legacy key
  for (const [key, config] of Object.entries(LEGACY_KEY_MAP)) {
    const value = localStorage.getItem(key);

    if (value === null) {
      continue;
    }

    hasMigrated = true;
    const store = getStore(config.store);

    if (!store) {
      console.warn(`[Migration] Unknown store: ${config.store}`);
      continue;
    }

    const transformedValue = config.transform ? config.transform(value, store) : value;

    if (config.action) {
      // Call action directly on store
      store[config.action](transformedValue);
    } else if (config.field && config.store === 'customer') {
      // Collect for batch customer update
      customerDetails[config.field] = transformedValue;
    } else if (config.field) {
      // Direct state assignment for other stores
      store[config.field] = transformedValue;
    }
  }

  // Batch update customer details
  if (Object.keys(customerDetails).length > 0) {
    const customerStore = getStore('customer');
    customerStore.setCustomerDetails(customerDetails);
  }

  // Remove all legacy keys (including sensitive ones)
  for (const key of ALL_LEGACY_KEYS) {
    localStorage.removeItem(key);
  }

  // Also remove any sensitive keys that might exist with different casing
  SENSITIVE_KEYS.forEach(key => {
    localStorage.removeItem(key);
    localStorage.removeItem(key.toLowerCase());
    localStorage.removeItem(key.toUpperCase());
  });

  // Remove old Vuex persistence key
  localStorage.removeItem('sb_sso_state');
  localStorage.removeItem('sb_sso_migrated');

  markMigrationComplete();

  if (hasMigrated) {
    console.log('[Migration] Successfully migrated localStorage to Pinia');
  } else {
    console.log('[Migration] No legacy data found, migration complete');
  }
}

/**
 * Clear all legacy localStorage keys without migrating
 * Useful for testing or forced cleanup
 */
export function clearLegacyStorage() {
  for (const key of ALL_LEGACY_KEYS) {
    localStorage.removeItem(key);
  }
  SENSITIVE_KEYS.forEach(key => {
    localStorage.removeItem(key);
  });
}

/**
 * Reset migration flag (for testing purposes)
 */
export function resetMigration() {
  localStorage.removeItem('sb_sso_migrated_pinia');
  localStorage.removeItem('sb_sso_migrated');
}

export default migrateLocalStorage;
